import React, { useCallback, useEffect, useRef, useState } from 'react';
import { bootApp, type AppContext } from '../app';
import { registerBuiltInSurfaces } from './surfaces/registry';
import { SurfaceStack } from './SurfaceStack';
import { Inspector } from './Inspector';
import { AmbientRail } from './AmbientRail';
import { UnlockSurface } from './UnlockSurface';
import { SASSTabBar } from './SASSTabBar';
import { SessionRecorder } from './SessionRecorder';
import { UpdatePrompt } from './UpdatePrompt';
import { AIFab } from './AIFab';
import { MascotSigil } from './MascotSigil';
import { NewsTicker } from './NewsTicker';
// Vite's explicit `?sharedworker` import: builds the worker to a separate
// ESM chunk + provides a SharedWorker constructor that points at the
// correct URL. The earlier pattern `new URL('./worker.ts', import.meta.url)`
// was failing inside a React component body — Vite couldn't statically
// detect it, so it fell through to the generic-asset path and shipped
// the worker as raw TypeScript inlined as a `data:video/mp2t;base64,…`
// URL. The browser couldn't execute that, so every unlock hung at
// "deriving root" until the 120s keyring timeout fired.
import KeyringSharedWorker from '../workers/keyring.worker.ts?sharedworker';

/**
 * Top-level UI shell.
 *
 * Three phases:
 *   - 'locked'    : show UnlockSurface, no AppContext yet
 *   - 'booting'   : keyring is unlocking + bootApp running
 *   - 'unlocked'  : full UI mounted, AppContext live
 *
 * On keyring lock event, transitions back to 'locked' and tears down ctx.
 */

type Phase =
  | { kind: 'locked' }
  | { kind: 'booting' }
  | { kind: 'unlocked'; ctx: AppContext; fingerprint: string; seed?: string };

export function AppShell(): JSX.Element {
  const [phase, setPhase] = useState<Phase>({ kind: 'locked' });
  const [error, setError] = useState<string | null>(null);
  const ctxRef = useRef<AppContext | null>(null);

  // U4 — apply persisted theme on boot. Phosphor (default) needs no
  // attribute set; warm requires data-theme="warm" on <html> for the
  // CSS cascade to pick up the alternate palette.
  useEffect(() => {
    try {
      const t = localStorage.getItem('alds-theme');
      if (t === 'warm') document.documentElement.setAttribute('data-theme', 'warm');
    } catch { /* noop */ }
  }, []);

  // Vite's `?sharedworker` import returns a class that builds + points at the
  // correctly-bundled worker chunk. Construct via this class in KeyringClient.connect.
  const workerUrl = KeyringSharedWorker;

  const handleUnlock = useCallback(async (params: {
    mode: 'generate' | 'unlock' | 'restore';
    password: string;
    seed?: string;
    backup?: Uint8Array;
    networkMode: 'local' | 'mesh';
  }) => {
    setError(null);
    setPhase({ kind: 'booting' });
    try {
      // SharedWorker may not exist in some test/dev environments. Fall back to in-proc.
      const useWorker = typeof SharedWorker !== 'undefined';
      const syncBackend = params.networkMode === 'mesh' ? 'orbitdb' as const : 'local' as const;

      const opts = useWorker
        ? (params.mode === 'unlock' && params.seed
          ? { mode: 'worker' as const, workerUrl, seed: params.seed, password: params.password, syncBackend }
          : { mode: 'worker' as const, workerUrl, password: params.password, syncBackend })
        : (params.mode === 'unlock' && params.seed
          ? { mode: 'inproc' as const, fresh: false, seed: params.seed, password: params.password, syncBackend }
          : { mode: 'inproc' as const, fresh: true, password: params.password, syncBackend });

      const { ctx, fingerprint, seed } = await bootApp(opts);
      ctxRef.current = ctx;
      registerBuiltInSurfaces(ctx);
      const next: Phase = { kind: 'unlocked', ctx, fingerprint };
      if (seed !== undefined) next.seed = seed;
      setPhase(next);

      // Phase 19.3: register the controller factory on the shared
      // CallManager singleton. Each ring/accept will now instantiate a
      // CallController bound to a real WebRtcMediaEngine driven by the
      // operator's network config.
      void (async () => {
        const { getCallManager } = await import('../calls/CallManager');
        const { CallController } = await import('../calls/CallController');
        const { WebRtcMediaEngine } = await import('../calls/MediaEngine');
        const { routeForCall, toEngineConfig } = await import('../calls/CallTransportPrefs');
        const { loadNetworkConfig, buildTurnIceServer } = await import('../calls/NetworkConfig');
        const cm = getCallManager(ctx);
        cm.setControllerFactory(({ session, remotePeerId, protocol }) => {
          const cfg = loadNetworkConfig();
          // Sync-build: we can't await inside this factory, so we use a
          // best-effort plan: no TURN by default. The CallActive renderer
          // (or initiate-call flow) is responsible for upgrading the engine
          // config via `controller.engine` if it wants TURN-relay routing.
          const plan = routeForCall({ sensitivity: cfg.defaultSensitivity });
          const engineConfig = toEngineConfig(plan);
          const engine = new WebRtcMediaEngine(engineConfig);
          // Background-resolve the TURN creds and patch the engine if
          // sensitivity ≥ medium. Best-effort; failures keep low-sensitivity.
          if (cfg.defaultSensitivity !== 'low' && cfg.turnUrl !== '') {
            void buildTurnIceServer(cfg).then((turn) => {
              if (turn === undefined) return;
              // Engine is already created with no TURN; we can't reconfigure
              // RTCPeerConnection.iceServers after the fact, so this becomes
              // future work. For now we surface a console warning.
              // eslint-disable-next-line no-console
              console.warn('[call-factory] TURN config available but engine already created without it. Configure default sensitivity = high on the network-config surface before placing the call.');
            });
          }
          return new CallController({ session, engine, protocol, remotePeerId });
        });
      })();

      // Phase 54.b (v11): start the presence heartbeat once the local
      // neighborhood is known. Without this the presence_status table is
      // never populated in production and every PresenceDot stays gray.
      // Best-effort — if the neighborhood isn't bootstrapped yet, surfaces
      // can still re-trigger this from the neighborhood-setup flow.
      void (async () => {
        try {
          const { getPresenceManager } = await import('../mesh/PresenceManager');
          const { MeshManager } = await import('../mesh/MeshManager');
          const mm = new MeshManager(ctx);
          const neighborhoodId = await mm.neighborhoodId();
          if (typeof neighborhoodId === 'string' && neighborhoodId.length > 0) {
            await getPresenceManager(ctx).start(neighborhoodId);
          }
        } catch {
          // No neighborhood yet, or transient sync error — dots stay gray
          // until presence can start. Re-attempt naturally on next unlock.
        }
      })();

      // Phase 55.b (v11): start the prepublish-vote auto-finalize poller so
      // drafts that hit approve-quorum (or reject-quorum, or expiry) finalize
      // without requiring a UI surface to explicitly call tryFinalize. The
      // poller is idempotent + GC-bound to the AppContext.
      void (async () => {
        try {
          const { getPendingPostPoller } = await import('../social/ModerationManager');
          getPendingPostPoller(ctx).start();
        } catch { /* swallow */ }
      })();

      // Phase 69.B: start the news-aggregator poller. Endpoint is the
      // operator's daemon at alds-news.bizlaunch.now by default; can be
      // overridden via localStorage["alds-news-aggregator-endpoint"]
      // for dev / alt-operator use. Fetch errors are swallowed by the
      // poller — daemon being offline just means no new headlines.
      void (async () => {
        try {
          const { getNewsAggregatorPoller } = await import('../news/NewsAggregatorPoller');
          let endpoint = 'https://alds-news.bizlaunch.now';
          try {
            const override = localStorage.getItem('alds-news-aggregator-endpoint');
            if (override !== null && override !== '') endpoint = override;
          } catch { /* localStorage unavailable */ }
          getNewsAggregatorPoller(ctx, { endpoint }).start();
        } catch { /* swallow */ }
      })();

      // Wire lock event to tear down UI. Both modes have a keyring now,
      // so this is unconditional.
      ctx.keyring.onLock(() => {
        const live = ctxRef.current;
        if (live !== null) {
          // Phase 54.b: stop emitting heartbeats while locked.
          void (async () => {
            try {
              const { getPresenceManager } = await import('../mesh/PresenceManager');
              getPresenceManager(live).stop();
            } catch { /* swallow */ }
          })();
          // Phase 55.b: stop the auto-finalize poller while locked.
          void (async () => {
            try {
              const { getPendingPostPoller } = await import('../social/ModerationManager');
              getPendingPostPoller(live).stop();
            } catch { /* swallow */ }
          })();
          // Phase 69.B: stop the news-aggregator poller while locked.
          void (async () => {
            try {
              const { getNewsAggregatorPoller } = await import('../news/NewsAggregatorPoller');
              // Endpoint here doesn't matter — the singleton is keyed
              // on AppContext, not on options. We just need a handle to
              // call .stop().
              getNewsAggregatorPoller(live, { endpoint: 'about:blank' }).stop();
            } catch { /* swallow */ }
          })();
        }
        ctxRef.current = null;
        setPhase({ kind: 'locked' });
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase({ kind: 'locked' });
    }
  }, [workerUrl]);

  const handleLock = useCallback(async () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    await ctx.keyring.lock();
    ctxRef.current = null;
    setPhase({ kind: 'locked' });
  }, []);

  // Keep keyring activity alive on user input
  useEffect(() => {
    if (phase.kind !== 'unlocked') return;
    const ctx = phase.ctx;
    const touch = () => { void ctx.keyring.touch(); };
    window.addEventListener('pointerdown', touch);
    window.addEventListener('keydown', touch);
    return () => {
      window.removeEventListener('pointerdown', touch);
      window.removeEventListener('keydown', touch);
    };
  }, [phase]);

  // Phase 58.b (v11): listen for incoming-call messages from two sources:
  //
  //   1. BroadcastChannel('alds-call') — posted by ThreadInbox when a
  //      call-ring envelope lands on a thread we belong to (foreground
  //      cross-tab path).
  //   2. navigator.serviceWorker — posted by src/sw.ts on push receive
  //      ('incoming-call' message). Wakes up backgrounded tabs whose
  //      push subscription routed through the operator's relay.
  //
  // Either path bridges to the call-incoming surface and stashes the
  // callId in localStorage so the surface picks it up on mount.
  useEffect(() => {
    if (phase.kind !== 'unlocked') return;
    const ctx = phase.ctx;
    const handleIncoming = (data: unknown): void => {
      const d = data as { type?: string; callId?: string } | undefined;
      if (d === undefined) return;
      if (d.type !== 'incoming' && d.type !== 'incoming-call') return;
      if (typeof d.callId !== 'string') return;
      try { localStorage.setItem('alds-pending-call', d.callId); } catch { /* noop */ }
      try {
        ctx.director.bridge('call-incoming', `ringing ${d.callId.slice(0, 8)}…`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`AppShell: bridge to call-incoming failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    };
    const cleanups: Array<() => void> = [];
    if (typeof globalThis.BroadcastChannel === 'function') {
      const channel = new globalThis.BroadcastChannel('alds-call');
      const onChannel = (ev: MessageEvent): void => handleIncoming(ev.data);
      channel.addEventListener('message', onChannel);
      cleanups.push(() => {
        channel.removeEventListener('message', onChannel);
        try { channel.close(); } catch { /* noop */ }
      });
    }
    if (typeof navigator !== 'undefined' && navigator.serviceWorker !== undefined) {
      const onSwMessage = (ev: MessageEvent): void => handleIncoming(ev.data);
      navigator.serviceWorker.addEventListener('message', onSwMessage);
      cleanups.push(() => {
        navigator.serviceWorker.removeEventListener('message', onSwMessage);
      });
    }
    return () => {
      for (const fn of cleanups) fn();
    };
  }, [phase]);

  return (
    <div className={phase.kind === 'unlocked' ? 'app-shell with-sass-tabs' : 'app-shell'}>
      <style>{styles}</style>

      {/* Phase 69.A — news headlines ticker. Sits above the app header
       * so it's the topmost band of chrome. Hidden when there are no
       * active headlines (empty state doesn't steal viewport). */}
      {phase.kind === 'unlocked' && <NewsTicker ctx={phase.ctx} />}

      <header className="app-header">
        <h1 className="t-serif app-title">
          ALDS <em>/</em> Alternative Local Dispatch
        </h1>
        <div className="t-meta app-meta">
          {phase.kind === 'unlocked' && (
            <>
              <div>identity <span className="t-phos">{phase.fingerprint}</span></div>
              <div>model <span className="t-phos">{phase.ctx.identityModel}</span></div>
            </>
          )}
          {phase.kind === 'locked' && <div>status <span className="t-phos">locked</span></div>}
          {phase.kind === 'booting' && <div>status <span className="t-phos">booting…</span></div>}
        </div>
      </header>

      {phase.kind === 'unlocked' && (
        <AmbientRail ctx={phase.ctx} onLock={handleLock} />
      )}

      <main className="app-main">
        {phase.kind === 'locked' && (
          <UnlockSurface onSubmit={handleUnlock} error={error} />
        )}
        {phase.kind === 'booting' && (
          <div className="booting schematic-frame">
            <div className="t-meta">booting</div>
            <div className="t-serif booting-line">
              deriving root <span className="cursor-tick">█</span>
            </div>
            <div className="t-meta dim">Argon2id · 64 MB · ~1–3s (WASM)</div>
          </div>
        )}
        {phase.kind === 'unlocked' && (
          <div className="app-grid">
            <SurfaceStack ctx={phase.ctx} />
            <Inspector ctx={phase.ctx} seed={phase.seed} />
          </div>
        )}
      </main>

      {/* Mobile-only backup banner — Inspector is hidden on mobile so the
       * critical seed-backup warning needs its own visible affordance.
       * Same dismiss key as Inspector's panel so a single acknowledge
       * applies to both views. */}
      {phase.kind === 'unlocked' && phase.seed !== undefined && (
        <MobileBackupBanner seed={phase.seed} />
      )}

      {phase.kind === 'unlocked' && <SASSTabBar ctx={phase.ctx} />}

      {/* Phase 70 — Personal AI helper FAB. Floats above SASSTabBar,
       * visible across every surface except personal-ai-chat itself. */}
      {phase.kind === 'unlocked' && <AIFab ctx={phase.ctx} />}

      {/* Phase 71 partial — moderation mascot sigil. Top-right pill,
       * visible across every surface except the mascot drawer itself. */}
      {phase.kind === 'unlocked' && <MascotSigil ctx={phase.ctx} />}

      {/* Tier 3 SCENE — in-app session recorder. Idle-state is a small
       * "● rec" pill in the top-right; active-state shows event count
       * and a stop+download button. Ctrl+Shift+R toggles it for users
       * who want to start before navigating. */}
      {phase.kind === 'unlocked' && (
        <SessionRecorder ctx={phase.ctx} fingerprint={phase.fingerprint} />
      )}

      {/* PWA update prompt — toast appears bottom-center when the SW
       * has installed a new precache. Renders in every phase (locked /
       * booting / unlocked) so users see it even before they unlock. */}
      <UpdatePrompt />
    </div>
  );
}

/**
 * Mobile-only BACK UP SEED banner — surfaces the warning that lives inside
 * the Inspector panel, which is hidden on mobile (U1.4). Uses the same
 * `alds-backup-acknowledged-v1` key as Inspector so a dismiss in either
 * view applies to both.
 */
function MobileBackupBanner({ seed }: { seed: string }): JSX.Element | null {
  const [acknowledged, setAcknowledged] = useState<boolean>(() => {
    try { return localStorage.getItem('alds-backup-acknowledged-v1') !== null; }
    catch { return false; }
  });
  const [showSeed, setShowSeed] = useState(false);
  if (acknowledged) return null;
  const acknowledge = () => {
    try { localStorage.setItem('alds-backup-acknowledged-v1', new Date().toISOString()); } catch { /* noop */ }
    setAcknowledged(true);
  };
  return (
    <div className="mobile-backup-banner">
      <div className="mobile-backup-row">
        <span className="t-meta" style={{ color: 'var(--amber)' }}>⚠ back up your seed</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="pill" onClick={() => setShowSeed((v) => !v)}>{showSeed ? 'hide' : 'reveal'}</button>
          <button className="pill" onClick={acknowledge}>i&apos;ve backed it up</button>
        </div>
      </div>
      {showSeed && (
        <div className="mobile-backup-seed">{seed}</div>
      )}
    </div>
  );
}

const styles = `
.mobile-backup-banner {
  display: none;
  padding: 8px 12px;
  background: rgba(245, 166, 35, 0.08);
  border-top: 1px solid var(--amber);
  border-bottom: 1px solid var(--amber);
  font-size: 11px;
}
.mobile-backup-row {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
}
.mobile-backup-seed {
  margin-top: 8px;
  font-family: var(--mono);
  font-size: 10px;
  word-break: break-all;
  color: var(--ink);
  background: rgba(0, 0, 0, 0.4);
  padding: 6px 8px;
  border: 1px solid var(--rule);
}
@media (max-width: 900px) {
  .mobile-backup-banner { display: block; }
}

.app-shell {
  position: relative;
  z-index: 1;
  min-height: 100%;
  width: 100%;
  max-width: 100vw;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
@media (max-width: 600px) {
  .app-shell { padding: 12px; gap: 10px; }
}

.app-header {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  justify-content: space-between;
  align-items: flex-end;
  border-bottom: 1px solid var(--rule);
  padding-bottom: 12px;
}
.app-title { font-size: 26px; margin: 0; line-height: 1; }
.app-title em { font-style: italic; color: var(--phos); }
.app-meta { text-align: right; line-height: 1.7; }

/* U1.3b — compact header on mobile. The full title + 2-row meta panel
 * eat ~80px of viewport before any content. Collapse to a one-line
 * "ALDS · identity" strip so role cards and the active surface get
 * meaningfully more room above the fold. */
@media (max-width: 600px) {
  .app-shell { padding: 8px 12px; gap: 6px; }
  .app-title { font-size: 14px; }
  .app-title em { color: var(--phos-dim); }  /* less visual weight */
  .app-meta { text-align: left; font-size: 9px; line-height: 1.2; display: flex; gap: 10px; }
  .app-meta > div { white-space: nowrap; }
  .app-header { padding-bottom: 6px; gap: 4px 10px; flex-wrap: nowrap; }
}

.app-main { flex: 1; min-height: 0; }

.app-grid {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 24px;
  height: 100%;
}
@media (max-width: 900px) {
  .app-grid { grid-template-columns: 1fr; grid-template-rows: 1fr auto; gap: 14px; }
}

.booting {
  padding: 60px 40px;
  background: var(--bg-2);
  text-align: center;
}
.booting .t-meta { margin-bottom: 16px; }
.booting-line {
  font-size: 28px;
  margin-bottom: 12px;
  color: var(--ink);
}
.booting .dim { color: var(--ink-dim); }
.cursor-tick {
  color: var(--phos);
  animation: blink 1s steps(2) infinite;
}
@keyframes blink { 50% { opacity: 0; } }
`;
